import React, { useCallback, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import isEqual from 'lodash/isEqual';

import { getRecurringMeetings } from 'Shared/actions/user';
import { setRecurringMeeting } from 'Shared/actions/workspaces';
import { Workspace } from 'Shared/constants/types';
import { sanitizeFullName, toSentence } from 'Shared/helpers';
import { resyncCalendar } from 'Shared/api';

import RecurringMeetingItem from './SyncMeetingsModalRecurringMeeting/SyncMeetingsModalRecurringMeeting';
import SyncMeetingsModalBodyWrapper from '../SyncMeetingsModalBodyWrapper/SyncMeetingsModalBodyWrapper';
import WpButton from 'Wpds/components/button/WpButton';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';

type Props = {
  workspace: Workspace;
  countWorkspaces: number;
  currentWorkspaceIndex: number;
  showCountWorkspaces: boolean;
  showResyncButton: boolean;
  onNextLevel: Function;
};

function SyncMeetingsModalBody({
  workspace,
  onNextLevel,
  countWorkspaces,
  currentWorkspaceIndex,
  showResyncButton,
  showCountWorkspaces,
}: Props) {
  const dispatch = useDispatch();
  const { user, id: currentUserId } = useCurrentUser();
  const currentUserEmail = user.email;
  const recurringMeetings = useSelector(state => state.user.recurringMeetings);
  const { id: workspaceId, users, displayLabel, isGroup } = workspace || { users: [] };
  const activeRecurringMeetings = recurringMeetings.filter(({ cancelled }) => !cancelled);
  const teammates = users.filter(({ id }) => id !== currentUserId);
  const [meetings, setMeetings] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [resyncingCalendar, setResyncingCalendar] = useState(false);
  const [calendarHasBeenResynced, setCalendarHasBeenResynced] = useState(false);

  const attendeesWhichShouldBe = [currentUserEmail, ...teammates.map(({ email }) => email)];
  const recurringMeetingsWhereTeammatesAttend = activeRecurringMeetings.filter(({ attendees }) =>
    isEqual(attendeesWhichShouldBe.sort(), attendees.map(({ email }) => email).sort())
  );
  const recurringMeetingsWithTeammates =
    recurringMeetingsWhereTeammatesAttend.length && teammates.length
      ? recurringMeetingsWhereTeammatesAttend
      : activeRecurringMeetings.filter(
          ({ name }) =>
            name &&
            name
              .toLowerCase()
              .includes(
                isGroup
                  ? displayLabel.toLowerCase()
                  : teammates[0]?.firstName?.toLowerCase() || displayLabel.split(' ')[0]?.toLowerCase()
              )
        );
  const filteredRecurringMeetings = searchValue
    ? activeRecurringMeetings.filter(({ name }) => name.toLowerCase().includes(searchValue.toLowerCase()))
    : recurringMeetingsWithTeammates;
  const showViewMoreButton = meetings.length < filteredRecurringMeetings.length;

  useEffect(() => {
    setMeetings(filteredRecurringMeetings.slice(0, 2));
  }, [workspaceId, searchValue, recurringMeetings]);

  const handleSearchValueChange = useCallback(event => setSearchValue(event.target.value), []);

  const handleViewMoreClick = () => setMeetings(filteredRecurringMeetings);

  const handleSelectMeeting = useCallback(
    recurringMeeting => {
      dispatch(
        setRecurringMeeting({
          recurringMeeting,
          workspaceId,
          page: showCountWorkspaces ? 'calendar_modal' : 'workspace',
        })
      );
      onNextLevel();
    },
    [workspaceId, onNextLevel]
  );

  const handleResyncCalendar = () => {
    setResyncingCalendar(true);
    resyncCalendar()
      .then(() => dispatch(getRecurringMeetings()))
      .then(() => {
        setResyncingCalendar(false);
        setCalendarHasBeenResynced(true);
      });
  };

  const getTeammatesLabel = useCallback(() => {
    if (!isGroup) return displayLabel;
    return toSentence(teammates.map(({ fullName, email }) => sanitizeFullName(fullName) || email));
  }, [isGroup, displayLabel, teammates]);

  const teammatesLabel = getTeammatesLabel();

  if (!workspace) onNextLevel();

  return (
    <SyncMeetingsModalBodyWrapper
      description={
        <>
          {`Which meeting is ${isGroup ? '' : 'your 1:1 with'} ${displayLabel}? `}
          {showCountWorkspaces && <span>{`(${currentWorkspaceIndex} of ${countWorkspaces})`}</span>}
        </>
      }
      searchValue={searchValue}
      onSearchValueChange={handleSearchValueChange}
      showViewMoreButton={showViewMoreButton}
      recurringMeetingsLabel={`Recurring Meetings ${!searchValue && teammatesLabel ? ` With ${teammatesLabel}` : ''}`}
      viewMoreButtonText={`View more meetings${!searchValue && teammatesLabel ? ` with ${teammatesLabel}` : ''}`}
      onViewMoreClick={handleViewMoreClick}
    >
      {meetings.length ? (
        meetings.map(meeting => (
          <RecurringMeetingItem
            key={meeting.id}
            meeting={meeting}
            teammates={teammates}
            currentUserEmail={currentUserEmail}
            onSelectMeeting={handleSelectMeeting}
          />
        ))
      ) : (
        <div className="text-center mt-4">
          {`No Recurring Meetings${!searchValue && teammatesLabel ? ` with ${teammatesLabel}` : ''} found`}
          {showResyncButton && (
            <div className="mt-4">
              {!calendarHasBeenResynced && (
                <WpButton variant="secondary" onClick={handleResyncCalendar} loading={resyncingCalendar}>
                  Resync Calendar
                </WpButton>
              )}
              {calendarHasBeenResynced && (
                <WpButton variant="secondary" disabled>
                  Calendar sync complete
                </WpButton>
              )}
            </div>
          )}
        </div>
      )}
    </SyncMeetingsModalBodyWrapper>
  );
}

export default SyncMeetingsModalBody;
